import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'
import { controlClass } from './controlClass'
import { DateInput } from './DateInput'

export { controlClass }

type InputProps = InputHTMLAttributes<HTMLInputElement> & { leading?: ReactNode; trailing?: ReactNode }

/**
 * The element tree is identical whether or not adornments are present, so an
 * adornment appearing mid-typing (a clear button, a unit) never remounts the
 * <input> and never drops focus or the caret.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, leading, trailing, ...props },
  ref,
) {
  // Every date field in the app gets the typed-or-picked date control.
  if (props.type === 'date' || props.type === 'datetime-local') return <DateInput ref={ref} className={className} {...props} />
  return (
    <div className="relative flex min-w-0 items-center">
      {/* Above the field: its backdrop blur paints it like a positioned box, over an icon placed before it. */}
      {leading ? <span className="pointer-events-none absolute left-2.5 z-[1] flex text-subtle">{leading}</span> : null}
      <input
        ref={ref}
        className={cn(controlClass, 'h-[calc(var(--spacing)*9*var(--ui-size))]', leading && 'pl-8.5', trailing && 'pr-9', className)}
        {...props}
      />
      {trailing ? <span className="absolute right-1.5 flex">{trailing}</span> : null}
    </div>
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 3, ...props }, ref) {
    return <textarea ref={ref} rows={rows} className={cn(controlClass, 'resize-y py-2 leading-relaxed', className)} {...props} />
  },
)
